import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  MapPin,
  BedDouble,
  Building2,
  FileText,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Layers,
  Map as MapIcon,
  ExternalLink,
} from "lucide-react";
import LeadCaptureModal from "../components/common/LeadCaptureModal";
import { LEAD_CHANNELS } from "../constants/data";
import {
  projectsAPI,
  mapProjectToProperty,
  projectImages,
  builderFolderImages,
  builderSlug,
} from "../services/api";

const GOLD = "#50C878";

export default function PropertyDetailPage() {
  const { id } = useParams(); // route is /property/:id (id = project slug)
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [images, setImages] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await projectsAPI.get(id);
        if (!alive) return;

        // The backend may return { data: {...} } or the row directly.
        const row = res?.data || res;
        if (!row || (Array.isArray(row) && !row.length)) {
          setError("Property not found");
          setLoading(false);
          return;
        }
        const mapped = mapProjectToProperty(Array.isArray(row) ? row[0] : row);
        setProperty(mapped);
        // Seed the gallery with whatever the mapper produced (stock fallback).
        setImages(mapped.images || []);

        // Prefer THIS project's own Supabase image folder. Only fall back to
        // the builder's full gallery (all its projects' photos) if this
        // specific project has no folder/images of its own.
        if (mapped.builderRaw && mapped.projectRaw) {
          const ownGallery = await projectImages(mapped.builderRaw, mapped.projectRaw);
          if (alive && ownGallery && ownGallery.length) {
            setImages(ownGallery);
            setActiveImg(0);
          } else if (mapped.builderRaw) {
            const gallery = await builderFolderImages(mapped.builderRaw);
            if (alive && gallery && gallery.length) {
              setImages(gallery);
              setActiveImg(0);
            }
          }
        }
      } catch (err) {
        if (alive) setError(err.message || "Failed to load property");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  function prevImg() {
    setActiveImg((i) => (images.length ? (i - 1 + images.length) % images.length : 0));
  }
  function nextImg() {
    setActiveImg((i) => (images.length ? (i + 1) % images.length : 0));
  }

  if (loading) {
    return (
      <div style={{ background: "#F2F8F3", minHeight: "100vh", paddingTop: "120px", textAlign: "center" }}>
        <div style={{ width: "44px", height: "44px", border: "3px solid rgba(80,200,120,0.3)", borderTopColor: GOLD, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
        <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter', sans-serif", marginTop: "20px" }}>Loading property…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div style={{ background: "#F2F8F3", minHeight: "100vh", paddingTop: "120px", textAlign: "center", padding: "120px 24px" }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", color: "#013220" }}>
          {error || "Property not found"}
        </p>
        <button
          onClick={() => navigate("/properties")}
          style={{ marginTop: "20px", background: "linear-gradient(135deg, #50C878, #7FD9A0)", color: "#FFFFFF", border: "none", padding: "12px 28px", borderRadius: "6px", fontWeight: 700, cursor: "pointer", fontFamily: "'Inter', sans-serif" }}
        >
          Back to Properties
        </button>
      </div>
    );
  }

  const heroImg = images[activeImg] || property.images?.[0];

  const facts = [
    property.bhk && { icon: BedDouble, label: "Configuration", value: property.bhk },
    property.type && { icon: Building2, label: "Type", value: property.type },
    property.area && { icon: MapPin, label: "Area", value: property.area },
    property.zone && { icon: MapIcon, label: "Zone", value: property.zone },
    property.units && { icon: Layers, label: "Units", value: property.units },
    property.acres && { icon: Layers, label: "Extent", value: property.acres },
    property.floors && { icon: Building2, label: "Floors", value: property.floors },
  ].filter(Boolean);

  return (
    <div style={{ background: "#F2F8F3", color: "#013220", minHeight: "100vh", paddingTop: "68px" }}>
      <div style={{ maxWidth: "1180px", margin: "0 auto", padding: "24px 24px 64px" }}>
        {/* Back link */}
        <button
          onClick={() => navigate(-1)}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "transparent", border: "none", color: GOLD, cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: "0.88rem", marginBottom: "20px", padding: 0 }}
        >
          <ArrowLeft size={16} /> Back
        </button>

        {/* Gallery */}
        <div style={{ position: "relative", borderRadius: "14px", overflow: "hidden", background: "#FFFFFF", border: "1px solid rgba(80,200,120,0.2)" }}>
          <div style={{ position: "relative", height: "min(56vh, 520px)", background: "#EDE8DA" }}>
            {heroImg && (
              <img
                src={heroImg}
                alt={property.title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}

            {images.length > 1 && (
              <>
                <button onClick={prevImg} aria-label="Previous image" style={navBtnStyle("left")}>
                  <ChevronLeft size={22} />
                </button>
                <button onClick={nextImg} aria-label="Next image" style={navBtnStyle("right")}>
                  <ChevronRight size={22} />
                </button>
                <div style={{ position: "absolute", bottom: "12px", left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: "20px", padding: "4px 12px", fontSize: "0.78rem", fontFamily: "'Inter', sans-serif" }}>
                  {activeImg + 1} / {images.length}
                </div>
              </>
            )}

            <div style={{ position: "absolute", top: "14px", right: "14px", background: "rgba(96,165,250,0.15)", color: "#1B4A7A", borderRadius: "4px", padding: "5px 12px", fontSize: "0.74rem", fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
              {property.status}
            </div>
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div style={{ display: "flex", gap: "8px", padding: "12px", overflowX: "auto", background: "#F7FBF8" }}>
              {images.map((src, i) => (
                <button
                  key={src + i}
                  onClick={() => setActiveImg(i)}
                  style={{
                    flex: "0 0 auto",
                    width: "84px",
                    height: "60px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: i === activeImg ? `2px solid ${GOLD}` : "2px solid transparent",
                    cursor: "pointer",
                    padding: 0,
                    background: "#EDE8DA",
                  }}
                >
                  <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Header + content layout */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "32px", marginTop: "28px" }} className="detail-grid">
          {/* Main column */}
          <div>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              {property.city || "Hyderabad"}
            </span>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", fontWeight: 600, margin: "6px 0 6px", lineHeight: 1.15 }}>
              {property.title}
            </h1>
            <p style={{ color: "rgba(1,50,32,0.7)", fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", marginBottom: "8px" }}>
              by{" "}
              {property.builderRaw ? (
                <Link to={`/builder/${builderSlug(property.builderRaw)}`} style={{ color: GOLD, textDecoration: "none", fontWeight: 600 }}>
                  {property.builder}
                </Link>
              ) : (
                property.builder
              )}
            </p>

            {property.address && (
              <p style={{ display: "flex", alignItems: "center", gap: "6px", color: "rgba(1,50,32,0.75)", fontFamily: "'Inter', sans-serif", fontSize: "0.9rem", marginBottom: "20px" }}>
                <MapPin size={15} color={GOLD} /> {property.address}
              </p>
            )}

            {/* Fact grid */}
            {facts.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginBottom: "28px" }}>
                {facts.map(({ icon: Icon, label, value }) => (
                  <div key={label} style={{ background: "#FFFFFF", border: "1px solid rgba(80,200,120,0.2)", borderRadius: "10px", padding: "14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "rgba(1,50,32,0.55)", fontSize: "0.72rem", fontFamily: "'Inter', sans-serif", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px" }}>
                      <Icon size={13} color={GOLD} /> {label}
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.95rem", color: "#013220" }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Description */}
            {property.description && (
              <div style={{ marginBottom: "28px" }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", fontWeight: 600, marginBottom: "10px" }}>
                  About this project
                </h2>
                <p style={{ color: "rgba(1,50,32,0.8)", fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", lineHeight: 1.7, whiteSpace: "pre-line" }}>
                  {property.description}
                </p>
              </div>
            )}

            {/* Builder intro */}
            {property.builderIntro && property.builderIntro !== property.description && (
              <div style={{ marginBottom: "28px" }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", fontWeight: 600, marginBottom: "10px" }}>
                  About the builder
                </h2>
                <p style={{ color: "rgba(1,50,32,0.8)", fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", lineHeight: 1.7, whiteSpace: "pre-line" }}>
                  {property.builderIntro}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar CTA */}
          <aside>
            <div style={{ position: "sticky", top: "90px", background: "#FFFFFF", border: "1px solid rgba(80,200,120,0.25)", borderRadius: "14px", padding: "22px" }}>
              {/* <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", color: "rgba(1,50,32,0.55)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "4px" }}>
                Pricing
              </p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", fontWeight: 600, color: "#013220", marginBottom: "16px" }}>
                {property.price || "Group-negotiated price"}
              </p> */}

              <button
                onClick={() => setShowModal(true)}
                style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg, #50C878, #7FD9A0)", border: "none", borderRadius: "8px", color: "#FFFFFF", fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "0.92rem", cursor: "pointer", marginBottom: "10px" }}
              >
                I'm Interested
              </button>

              {/* {property.brochure && (
                <button
                  onClick={() => window.open(property.brochure, "_blank")}
                  style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid rgba(80,200,120,0.45)", borderRadius: "8px", color: GOLD, fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
                >
                  <FileText size={15} /> View Brochure
                </button>
              )} */}

              {property.googleMapLink && (
                <a
                  href={property.googleMapLink}
                  target="_blank"
                  rel="noreferrer"
                  style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid rgba(80,200,120,0.45)", borderRadius: "8px", color: GOLD, fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", boxSizing: "border-box" }}
                >
                  <MapIcon size={15} /> View on Map
                </a>
              )}

              {/* {property.websiteLink && (
                <a
                  href={property.websiteLink}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "rgba(1,50,32,0.6)", fontFamily: "'Inter', sans-serif", fontSize: "0.82rem", textDecoration: "none", marginTop: "14px" }}
                >
                  Official website <ExternalLink size={13} />
                </a>
              )} */}
            </div>
          </aside>
        </div>
      </div>

      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        propertyTitle={property.title}
        propertyId={property.id}
        channel={LEAD_CHANNELS.WEB}
      />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 860px) {
          .detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function navBtnStyle(side) {
  return {
    position: "absolute",
    top: "50%",
    [side]: "12px",
    transform: "translateY(-50%)",
    background: "rgba(0,0,0,0.45)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };
}